import {
  getCumulativeLinearTransform,
  getMatrixScaleX,
  getMatrixScaleY,
  intrinsicAabbUnderMatrix,
  isIdentityMatrix,
} from "./transformMatrix.ts";

export interface ElementRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * CSS style object ready to spread onto an absolutely-positioned overlay so
 * that, after the transform is applied, its visual AABB matches the element's
 * bounding rect. All consumers should prefer this over reading the underlying
 * geometry fields directly.
 */
export interface OverlayPositionStyle {
  position: "absolute";
  top: number;
  left: number;
  width: number;
  height: number;
  transform: string;
  transformOrigin: "0 0";
}

/**
 * Returns `element`'s bounding rect translated so its origin is relative to
 * `container`'s top-left. When `container` is null, returns the rect in its
 * native coordinate space (typically viewport).
 */
export const getElementContainerRelativeRect = (element: HTMLElement, container: HTMLElement | null): ElementRect => {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container?.getBoundingClientRect();
  return {
    top: elementRect.top - (containerRect?.top ?? 0),
    left: elementRect.left - (containerRect?.left ?? 0),
    width: elementRect.width,
    height: elementRect.height,
  };
};

export interface OverlayGeometry {
  /** Axis-aligned bounding rect in container coords (matches `getBoundingClientRect`). */
  boundingRect: ElementRect;
  /**
   * Where to position an overlay so that, after applying `transform` with
   * `transform-origin: 0 0`, its visual AABB matches `boundingRect`. Sized at
   * the element's intrinsic (pre-transform) layout dimensions.
   *
   * Mixed coordinate space — be careful when reading fields directly:
   *   - `top`/`left` are in container/screen coords (derived from
   *     `getBoundingClientRect()`, so they include ancestor scale/zoom).
   *   - `width`/`height` are in CSS pixels, unaffected by ancestor transforms.
   *     For non-rotated elements these are fractional (from `getBoundingClientRect`
   *     divided by scale). For rotated elements they fall back to `offsetWidth/Height`.
   *
   * The mixing is intentional: when this rect is used as an overlay style with
   * the matching `transform`, the rendered AABB equals `boundingRect`. For
   * non-rendering uses (e.g. recovering current element dimensions), prefer
   * `width`/`height` directly *without* dividing by canvas scale.
   */
  intrinsicRect: ElementRect;
  /** CSS transform string (e.g. `matrix(...)`) — `"none"` when identity. */
  transform: string;
  /** Always `"0 0"` — origin offsets are baked into `intrinsicRect`. */
  transformOrigin: "0 0";
  /** Pre-built CSS style for an overlay aligned with the element. */
  overlayStyle: OverlayPositionStyle;
  /** Magnitude of the transform's local-x basis vector — local-x scale factor. */
  scaleX: number;
  /** Magnitude of the transform's local-y basis vector — local-y scale factor. */
  scaleY: number;
  /**
   * Element's `border-top-left-radius` resolved to CSS px (uniform corners
   * assumed). `rx` and `ry` differ when the radius is percentage-based and the
   * element isn't square (e.g. `border-radius: 50%` on a 200×100 element →
   * `{ rx: 100, ry: 50 }`).
   */
  borderRadius: { rx: number; ry: number };
  /**
   * Cumulative rotation in radians, clockwise (CSS convention). Derived from
   * the transform's local-x basis. Used to rotate resize cursors so they match
   * the visual edge orientation.
   */
  rotation: number;
}

const buildOverlayStyle = (intrinsicRect: ElementRect, transform: string): OverlayPositionStyle => ({
  position: "absolute",
  top: intrinsicRect.top,
  left: intrinsicRect.left,
  width: intrinsicRect.width,
  height: intrinsicRect.height,
  transform,
  transformOrigin: "0 0",
});

/**
 * Returns the geometry needed to render an overlay (selection border, resize
 * handles, drag clone, etc.) that visually aligns with an arbitrarily-
 * transformed element.
 *
 * Math: transform the four intrinsic corners through the cumulative linear
 * matrix `M`, take the AABB min, then place the overlay at
 * `boundingRect.{left,top} - {minX,minY}`. With `transform: M` and
 * `transform-origin: 0 0` on the overlay, its visual AABB equals the
 * element's `getBoundingClientRect()`. The element's own `transform-origin`
 * doesn't matter — translations don't change parallelogram shape, and we
 * compensate via positioning.
 *
 * Container handling: the cumulative transform walks ancestors *exclusive*
 * of `container` — any transform applied **on `container` itself** is NOT
 * included in `transform`/`scaleX`/`scaleY`. Pick `container` as the closest
 * un-transformed ancestor whose coordinate system the overlay will render in.
 * For overlays rendered inside an editor surface, pass the editor element.
 * For clones rendered against the viewport (e.g. dnd-kit `DragOverlay` portaled
 * to `document.body`), pass `document.body`.
 */
export const getElementOverlayGeometry = (element: HTMLElement, container: HTMLElement | null): OverlayGeometry => {
  const boundingRect = getElementContainerRelativeRect(element, container);
  const matrix = getCumulativeLinearTransform(element, container);
  const identity = isIdentityMatrix(matrix);
  const scaleX = identity ? 1 : getMatrixScaleX(matrix);
  const scaleY = identity ? 1 : getMatrixScaleY(matrix);

  // offsetWidth/Height are integers — may be 1px less than the actual fractional rendered size
  // (common with flex layouts that distribute sub-pixel widths). For non-rotated elements, derive
  // the precise fractional intrinsic size from the bounding rect instead.
  // For rotated elements getBoundingClientRect reports AABB dimensions, so we must fall back.
  // A degenerate ancestor scale (e.g. scale(0)) makes scaleX/Y 0, so we also fall back to avoid
  // dividing by zero into Infinity/NaN.
  const layoutWidth = element.offsetWidth;
  const layoutHeight = element.offsetHeight;
  const hasRotation = !identity && (Math.abs(matrix.b) > 1e-9 || Math.abs(matrix.c) > 1e-9);
  const width = hasRotation || Math.abs(scaleX) < 1e-9 ? layoutWidth : boundingRect.width / scaleX;
  const height = hasRotation || Math.abs(scaleY) < 1e-9 ? layoutHeight : boundingRect.height / scaleY;

  let intrinsicLeft = boundingRect.left;
  let intrinsicTop = boundingRect.top;
  if (!identity) {
    const { minX, minY } = intrinsicAabbUnderMatrix(matrix, layoutWidth, layoutHeight);
    intrinsicLeft = boundingRect.left - minX;
    intrinsicTop = boundingRect.top - minY;
  }

  const intrinsicRect: ElementRect = { top: intrinsicTop, left: intrinsicLeft, width, height };
  const transform = identity ? "none" : matrix.toString();
  const rotation = identity ? 0 : Math.atan2(matrix.b, matrix.a);
  const borderRadius = resolveBorderTopLeftRadius(element, width, height);

  return {
    boundingRect,
    intrinsicRect,
    transform,
    transformOrigin: "0 0",
    overlayStyle: buildOverlayStyle(intrinsicRect, transform),
    scaleX,
    scaleY,
    borderRadius,
    rotation,
  };
};

/**
 * Build an overlay style for an arbitrary intrinsic-space rect using a
 * geometry's transform. Used to render resize-previews and other overlays
 * whose rect isn't the element's current intrinsic rect.
 */
export const buildOverlayStyleForRect = (rect: ElementRect, geometry: OverlayGeometry): OverlayPositionStyle =>
  buildOverlayStyle(rect, geometry.transform);

const resolveRadiusToken = (token: string, basis: number): number => {
  if (token.endsWith("%")) return (parseFloat(token) * basis) / 100;
  return parseFloat(token) || 0;
};

/**
 * Resolves `border-top-left-radius` to pixel rx/ry, applying CSS's uniform
 * overflow clamping (getComputedStyle doesn't).
 */
const resolveBorderTopLeftRadius = (
  element: HTMLElement,
  width: number,
  height: number,
): { rx: number; ry: number } => {
  const raw = getComputedStyle(element).borderTopLeftRadius.trim();
  if (!raw) return { rx: 0, ry: 0 };
  const [rxToken, ryToken = rxToken] = raw.split(/\s+/);
  let rx = resolveRadiusToken(rxToken, width);
  let ry = resolveRadiusToken(ryToken, height);

  // CSS clamps all radii by a single global factor, not per-axis like SVG does.
  if (rx > 0 && ry > 0) {
    const factor = Math.min(width / (2 * rx), height / (2 * ry), 1);
    if (factor < 1) {
      rx *= factor;
      ry *= factor;
    }
  }

  return { rx, ry };
};
